// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 2 — The ledger.
//
// The question: which hours were actually the worst — and does ranking by the biggest number
// give a different answer from ranking by how far it spread? The disagreement between the two
// orderings IS the finding, so both ranks are on every row, joined by a slope line.

import type { Bundle } from '../data';
import type { Episode } from '../types';
import { el, legend, panel, stat, statRow, svg, xScroll } from '../components/ui';
import { concentration, duration, number, ordinal, prettyHour, tip, UNIT } from '../format';
import { EPISODE_TYPE_COLOUR, JURISDICTION_COLOUR } from '../palette';
import { glossaryLink } from '../glossary';
import { hasEpisodeDetail } from '../router';
import { getState, setState } from '../state';
import { openStation } from './stationDetail';

type SortKey = 'peak' | 'reach' | 'recent' | 'span';

function slopeGlyph(episode: Episode, total: number): SVGSVGElement {
  // Two ranks, joined. A steep line means the two orderings disagree about this episode.
  const width = 54;
  const height = 26;
  const node = svg('svg', { viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });
  node.setAttribute('aria-label', `Rank ${episode.rankPeak} by peak, ${episode.rankReach} by reach`);
  const y = (rank: number) => 3 + (Math.min(rank, total) / total) * (height - 6);
  const flip = Math.abs(episode.rankPeak - episode.rankReach);
  const stroke = flip > total * 0.15 ? 'var(--status-warn)' : 'var(--border-strong)';
  node.append(svg('line', { x1: 4, y1: y(episode.rankPeak), x2: width - 4, y2: y(episode.rankReach), stroke, 'stroke-width': 1.5 }));
  node.append(svg('circle', { cx: 4, cy: y(episode.rankPeak), r: 2.5, fill: 'var(--status-bad)' }));
  node.append(svg('circle', { cx: width - 4, cy: y(episode.rankReach), r: 2.5, fill: 'var(--status-info)' }));
  return node;
}

function footprintGlyph(episode: Episode): SVGSVGElement {
  // A statewide event and a single-valley event should differ in SHAPE before you read a number.
  const size = 22;
  const node = svg('svg', { viewBox: `0 0 ${size} ${size}`, width: size, height: size, role: 'img' });
  node.setAttribute('aria-label', `${episode.clusterCount} clusters spanning ${episode.spanKm} km`);
  const radius = Math.min(9, 2 + Math.sqrt(episode.spanKm) / 3.6);
  node.append(
    svg('circle', {
      cx: size / 2, cy: size / 2, r: radius,
      fill: 'none',
      stroke: episode.suspect ? 'var(--text-muted)' : 'var(--status-warn)',
      'stroke-width': 1.4,
      'stroke-dasharray': episode.suspect ? '3 2' : '0',
    })
  );
  for (let i = 0; i < Math.min(episode.clusterCount, 10); i++) {
    const angle = (i / Math.min(episode.clusterCount, 10)) * Math.PI * 2;
    node.append(
      svg('circle', {
        cx: size / 2 + Math.cos(angle) * radius,
        cy: size / 2 + Math.sin(angle) * radius,
        r: 1.2,
        fill: 'var(--text-secondary)',
      })
    );
  }
  return node;
}

export function renderLedger({ bundle, container }: { bundle: Bundle; container: HTMLElement }): void {
  const { episodes, meta } = bundle;
  const state = getState();

  const intro = panel({
    title: 'The ledger',
    subtitle:
      'Every run of hours in which a monitoring cluster exceeded both the whole daily particle standard inside a single hour, and its own 95th percentile for that month. Ranked two ways at once, because the two orderings disagree.',
  });

  const confirmed = episodes.filter((episode) => !episode.suspect);
  const biggestFlip = episodes.reduce(
    (best, episode) =>
      Math.abs(episode.rankPeak - episode.rankReach) > Math.abs(best.rankPeak - best.rankReach) ? episode : best,
    episodes[0]
  );

  intro.body.append(
    statRow(
      stat(number(episodes.length), 'episodes detected', `since ${episodes.reduce((min, e) => Math.min(min, e.year), 9999)}`),
      stat(
        number(confirmed.length),
        'corroborated',
        'at least two clusters elevated at the peak hour',
        tip('The remainder are published and marked suspect rather than deleted — a single elevated cluster may be a real local event or a faulty instrument.')
      ),
      stat(
        concentration(episodes.reduce((max, e) => Math.max(max, e.peakValue), 0)),
        `worst hour, ${UNIT}`,
        'the highest single hourly PM2.5 reading in the archive'
      ),
      stat(
        number(Math.max(...episodes.map((e) => e.stationHours))),
        'widest reach',
        'cluster-hours elevated in one episode',
        tip('Reach counts every cluster in every hour it was elevated, so a long wide event outranks a brief intense one.')
      )
    )
  );

  if (biggestFlip) {
    intro.body.append(
      el(
        'p',
        'note',
        `<strong>The ranks disagree most about ${biggestFlip.peakClusterName}, ${prettyHour(
          biggestFlip.peakLocal
        )}</strong> — ${ordinal(biggestFlip.rankPeak)} by peak concentration but ${ordinal(
          biggestFlip.rankReach
        )} by reach. "Biggest" and "worst" are not the same question: a single instrument in a valley can register a higher number than a smoke plume covering a state.`
      )
    );
  }
  container.append(intro.root);

  const table = panel({
    title: 'Episodes',
    subtitle:
      'Sort by either rank. The slope line joins an episode’s two positions — a steep line means the orderings disagree. The ring shows how far it spread; a dashed ring means only one cluster saw it.',
  });

  let sort: SortKey = 'reach';
  let typeFilter = 'All';
  let showSuspect = true;

  const controls = el('div', 'control-row');

  const sortField = el('label', 'select-field');
  sortField.append(el('span', 'field-label', 'Rank by'));
  const sortSelect = el('select');
  sortSelect.setAttribute('aria-label', 'Rank episodes by');
  for (const [value, label] of [
    ['reach', 'Reach — cluster-hours elevated'],
    ['peak', 'Peak concentration'],
    ['span', 'Distance spanned'],
    ['recent', 'Most recent'],
  ] as const) {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    if (value === sort) option.selected = true;
    sortSelect.append(option);
  }
  sortSelect.addEventListener('change', () => {
    sort = sortSelect.value as SortKey;
    draw();
  });
  sortField.append(sortSelect);
  controls.append(sortField);

  const types = ['All', 'Smoke', 'Dust', 'Winter inversion', 'Unclassified'];
  const typeRow = el('div', 'segmented');
  for (const type of types) {
    const button = el('button', `segment${type === typeFilter ? ' segment-on' : ''}`);
    button.type = 'button';
    button.textContent = type;
    button.setAttribute(
      'data-tip',
      type === 'All'
        ? 'Every episode'
        : `Classified from the PM2.5 to PM10 ratio, the season and the onset wind — all three numbers are on every row`
    );
    button.addEventListener('click', () => {
      typeFilter = type;
      [...typeRow.children].forEach((child, index) => child.classList.toggle('segment-on', types[index] === typeFilter));
      draw();
    });
    typeRow.append(button);
  }
  controls.append(typeRow);

  const suspectToggle = el('button', 'button');
  suspectToggle.type = 'button';
  suspectToggle.setAttribute('data-tip', 'Episodes seen by only one cluster may be real local events or instrument faults');
  suspectToggle.addEventListener('click', () => {
    showSuspect = !showSuspect;
    suspectToggle.classList.toggle('button-on', !showSuspect);
    draw();
  });
  controls.append(suspectToggle);
  table.body.append(controls);

  table.body.append(
    legend([
      ...Object.entries(EPISODE_TYPE_COLOUR).map(([label, hex]) => ({ label, hex })),
      { label: 'rank by peak', hex: 'var(--status-bad)' },
      { label: 'rank by reach', hex: 'var(--status-info)' },
    ])
  );

  const wrap = el('div', 'table-wrap');
  const node = el('table', 'data-table');
  node.innerHTML =
    '<thead><tr>' +
    '<th scope="col">Peak hour</th>' +
    '<th scope="col">Where</th>' +
    '<th scope="col">Type</th>' +
    '<th scope="col">Ranks</th>' +
    '<th scope="col">Spread</th>' +
    '<th scope="col" class="num">Peak</th>' +
    '<th scope="col" class="num">Reach</th>' +
    '<th scope="col" class="num">Lasted</th>' +
    '</tr></thead><tbody></tbody>';
  const tbody = node.querySelector('tbody') as HTMLElement;
  wrap.append(node);
  table.body.append(xScroll(wrap));

  const more = el('button', 'button');
  more.type = 'button';
  let limit = 40;

  function draw(): void {
    const rows = episodes
      .filter((episode) => typeFilter === 'All' || episode.type === typeFilter)
      .filter((episode) => showSuspect || !episode.suspect)
      .sort((a, b) => {
        if (sort === 'peak') return a.rankPeak - b.rankPeak;
        if (sort === 'span') return b.spanKm - a.spanKm;
        if (sort === 'recent') return b.start - a.start;
        return a.rankReach - b.rankReach;
      });

    suspectToggle.textContent = showSuspect ? 'Hide suspect episodes' : 'Show suspect episodes';
    tbody.innerHTML = '';
    for (const episode of rows.slice(0, limit)) {
      const tr = el('tr');
      if (episode.id === state.episode) tr.classList.add('row-on');
      tr.style.cursor = 'pointer';
      tr.setAttribute(
        'data-tip',
        tip(
          `${episode.type}${episode.suspect ? ' — suspect, one cluster only' : ''}`,
          `Fine fraction ${episode.fineFraction === null ? 'not measurable' : episode.fineFraction.toFixed(2)} (PM2.5 ÷ PM10)`,
          `Onset wind ${episode.onsetWind === null ? 'not measured' : `${episode.onsetWind} m/s`}`,
          `${episode.clusterCount} clusters, ${episode.clustersAtPeak} elevated at the peak hour`,
          episode.frontKmH !== null ? `Front speed ${episode.frontKmH} km/h (R² ${episode.frontR2})` : 'Front speed not estimable',
          hasEpisodeDetail(episode.rankReach)
            ? 'Click to replay it on the map'
            : 'Click to open the station — this episode is ranked outside the top 200 by reach, so no hourly replay is published for it'
        )
      );

      const when = el('td');
      when.innerHTML = `<span class="cell-name">${prettyHour(episode.peakLocal)}</span>`;
      const where = el('td');
      where.innerHTML = `<span class="cell-name">${episode.peakClusterName}</span>`;
      const jurisdiction = el('span', 'chip');
      jurisdiction.textContent = episode.jurisdiction;
      jurisdiction.style.color = JURISDICTION_COLOUR[episode.jurisdiction] ?? 'var(--text-secondary)';
      where.append(document.createTextNode(' '), jurisdiction);

      const type = el('td');
      const chip = el('span', 'chip chip-solid');
      chip.textContent = episode.suspect ? `${episode.type} ?` : episode.type;
      chip.style.background = EPISODE_TYPE_COLOUR[episode.type] ?? 'var(--border-strong)';
      if (episode.suspect) chip.style.opacity = '0.6';
      type.append(chip);

      const ranks = el('td');
      ranks.append(slopeGlyph(episode, episodes.length));

      const spread = el('td');
      spread.append(footprintGlyph(episode));
      spread.append(document.createTextNode(` ${episode.spanKm} km`));

      const peak = el('td', 'num');
      peak.textContent = concentration(episode.peakValue);
      const reach = el('td', 'num');
      reach.textContent = number(episode.stationHours);
      const lasted = el('td', 'num');
      lasted.textContent = duration(episode.durationHours);

      tr.append(when, where, type, ranks, spread, peak, reach, lasted);
      tr.addEventListener('click', () => {
        // Every row leads somewhere. Rows without a published hourly slice open the station that
        // recorded the peak instead of navigating to a replay that cannot be drawn.
        if (hasEpisodeDetail(episode.rankReach)) {
          setState({ episode: episode.id, view: 'map' });
          return;
        }
        const station = bundle.stations.find((candidate) => candidate.id === episode.peakClusterId);
        if (station) void openStation(station);
      });
      tbody.append(tr);
    }

    more.textContent =
      limit >= rows.length
        ? `Showing all ${number(rows.length)} matching episodes`
        : `Show more — ${number(rows.length - limit)} of ${number(rows.length)} not shown`;
    more.disabled = limit >= rows.length;
  }

  more.addEventListener('click', () => {
    limit += 60;
    draw();
  });
  draw();
  table.body.append(more);

  table.body.append(
    el(
      'p',
      'note',
      `Reach is cluster-hours: every cluster counted in every hour it was elevated. It is deliberately not a population figure — that would need a population join this site does not have, and "how many people were breathing this" is exactly the sort of headline that turns out to rest on one arbitrary radius. ` +
        `A ${glossaryLink('suspect', 'suspect episode')} is one no neighbour corroborated. Detection runs on concentration, never on the published category, because the category falls off the top of its own scale: the worst reading in this archive was returned with no category at all. ` +
        `Episodes are detected from PM2.5 above ${meta.smoke1.absoluteFloorUgM3} ${UNIT} for at least ${meta.smoke1.minHours} hours.`
    )
  );
  container.append(table.root);
}
